import { createListenerMiddleware, ListenerMiddlewareInstance, ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import * as Sentry from '@sentry/react';

import { TOP_BANNER_TYPE_CLOSED, BACKEND_ENV, BAN_VPN_URLS } from 'application/consts';
import { createLogger } from 'application/utils/LogUtils';
import AnalyticsService, { ParamNameAMP, RegistrationSourceValueMap } from 'application/services/AnalyticsService';
import PhoneUtils from 'application/utils/PhoneUtils';
import CompanyUtils from 'application/utils/CompanyUtils';

import { removeTokenCookie } from '@shared/lib';
import { UserDataType } from 'domain/auth/types';
import { RegistrationSource } from '@shared/api/graphql/generated';

import {
  clearAccountData,
  fetchCurrentUserData,
  initializeAccountData,
  updateIsWithoutCompanies,
} from '../accountSlice';
import { disconnectConfigFlagsSubscription } from '../configFlagsSlice/configFlagsListener';
import { disconnectRealtimeSocket } from '../realtimeSlice/realtimeListener';
import { setAppInitialized, setAppStatus, setAppBanned, setAuthToken, bootApp } from './slice';
import { ApplicationState } from './types';

const USER_INITIALIZED_ERROR = /User matching query does not exist./;
const USER_HAS_NOT_COMPANY_ERROR = /UserCompany with id \d+ does not exists/;
const AUTH_PERMISSION_ERROR =
  /You do not have permission to perform this action|Unauthorized|Authentication credentials were not provided/i;
const BAN_VPN_URL = BAN_VPN_URLS[BACKEND_ENV];
const VPN_CHECK_TIMEOUT = 5000;
const INIT_TIMEOUT_MS = 15_000;

const logger = createLogger('AppInitListener', '#ffe66d');

type AnyDispatch = ThunkDispatch<any, any, UnknownAction>;
type GetState = () => { application: ApplicationState; account: { userData?: UserDataType } };
type PersistorLike = { purge: () => Promise<any> };

let activePersistor: PersistorLike | null = null;
export const setPersistor = (p: PersistorLike) => {
  activePersistor = p;
};

let hasInitialized = false;
let lastInitializedCompanyId: string | null = null;
let bootDispatched = false;

const notifyParentFrame = (authToken: string | undefined) => {
  window.parent.postMessage(authToken ? 'LOGGED_IN' : 'LOGGED_OUT', '*');
};

const syncAnalytics = (userData?: UserDataType) => {
  Sentry.setUser(userData ? { id: userData.id, email: userData.email ?? undefined } : null);
  AnalyticsService.setUserId(userData?.id, userData?.isTest);

  try {
    AnalyticsService.setUserProperties({
      [ParamNameAMP.USER_ID]: userData?.id,
      [ParamNameAMP.COMPANY_ID]: userData?.mainCompany?.id,
      [ParamNameAMP.IS_TEST]: userData?.isTest,
      [ParamNameAMP.EMAIL]: userData?.email,
      [ParamNameAMP.PHONE_NUM]: userData?.phone,
      [ParamNameAMP.PHONE_NUM_COUNTRY]: userData?.phone
        ? PhoneUtils.parsePhoneNumberFromString(userData.phone)?.country
        : undefined,
      [ParamNameAMP.COMPANY_BUSINESS_SPHERE]: userData?.mainCompany?.businessSphereId,
      [ParamNameAMP.COMPANY_COUNTRY]: userData?.mainCompany?.country,
      [ParamNameAMP.COMPANY_INN]: CompanyUtils.getCompanyINNByCountry(userData?.mainCompany),
    });
  } catch (e) {
    logger.error('Failed to set analytics user properties:', e);
  }

  const registrationSource = userData?.registrationSource;
  const registrationSourceFromSB =
    registrationSource === RegistrationSource.SbBusinessId || registrationSource === RegistrationSource.SbOnline;

  if (registrationSourceFromSB) {
    AnalyticsService.setUserProperties({
      [ParamNameAMP.SIGN_UP_SOURCE]: RegistrationSourceValueMap[registrationSource],
    });
  }
};

const checkVpnBan = async (dispatch: AnyDispatch) => {
  if (window.location.pathname.includes('web-player')) {
    dispatch(setAppBanned(false));
    return;
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), VPN_CHECK_TIMEOUT);

  try {
    const response = await fetch(BAN_VPN_URL, { signal: controller.signal });
    clearTimeout(timeoutId);
    const result = await response.json();
    dispatch(setAppBanned(!result.ip_location_access_allowed));
  } catch (e) {
    clearTimeout(timeoutId);
    dispatch(setAppBanned(false));
  }
};

const handleInit = async (dispatch: AnyDispatch, getState: GetState, signal: AbortSignal) => {
  dispatch(setAppStatus('initializing'));
  logger.info('🚀 Starting app initialization');

  try {
    const userResult = await dispatch(fetchCurrentUserData()).unwrap();

    if (signal.aborted) return;

    const companyId = userResult?.currentCompanyId;

    if (!companyId) {
      logger.debug('⚠️ User has no company — setting ready');
      syncAnalytics(getState().account.userData);
      dispatch(setAppInitialized());
      dispatch(setAppStatus('ready'));
      return;
    }

    await dispatch(initializeAccountData(companyId)).unwrap();

    if (signal.aborted) return;

    hasInitialized = true;
    lastInitializedCompanyId = companyId;

    syncAnalytics(getState().account.userData);
    dispatch(setAppInitialized());
    dispatch(setAppStatus('ready'));
    logger.info('🎉 App fully initialized');
  } catch (error) {
    if (signal.aborted) return;

    logger.error('💥 Failed to initialize:', error);

    if (USER_INITIALIZED_ERROR.test(String(error))) {
      if (hasInitialized) {
        logger.warn('User initialization error — clearing auth data');
        dispatch(setAuthToken(undefined));
        return;
      }
      logger.warn('User initialization error on first attempt — likely backend race, skipping auth clear');
      dispatch(setAppInitialized());
      dispatch(setAppStatus('idle'));
      return;
    }

    if (AUTH_PERMISSION_ERROR.test(String(error))) {
      logger.warn('🔒 Auth/permission error during init — logging out');
      dispatch(setAuthToken(undefined));
      return;
    }

    dispatch(setAppInitialized());
    dispatch(setAppStatus('idle'));

    if (USER_HAS_NOT_COMPANY_ERROR.test(String(error))) {
      logger.warn('"UserCompany does not exist" — setting ready anyway');
      syncAnalytics(getState().account.userData);
      dispatch(updateIsWithoutCompanies(true));
      dispatch(setAppInitialized());
      dispatch(setAppStatus('ready'));
    }
  }
};

const handleLogout = async (
  dispatch: AnyDispatch,
  signal: AbortSignal,
  apolloClient: ApolloClient<NormalizedCacheObject>
) => {
  logger.info('🚪 Starting logout cleanup');

  try {
    dispatch(clearAccountData());
    logger.info('✅ Redux store cleared');

    await apolloClient.cache.reset();
    logger.info('✅ Apollo cache cleared');

    if (signal.aborted) return;

    removeTokenCookie();
    localStorage.removeItem(TOP_BANNER_TYPE_CLOSED);

    if (!signal.aborted && activePersistor) {
      await activePersistor.purge();
      logger.info('✅ Persist store purged');
    }

    logger.info('🎉 Logout cleanup completed');
  } catch (error) {
    logger.error('💥 Error during logout cleanup:', error);
  } finally {
    hasInitialized = false;
    lastInitializedCompanyId = null;
    disconnectRealtimeSocket();
    disconnectConfigFlagsSubscription();
    if (!signal.aborted) {
      syncAnalytics(undefined);
      dispatch(setAppStatus('idle'));
    }
  }
};

type CreateAppInitListenerType = (
  apolloClient: ApolloClient<NormalizedCacheObject>
) => ListenerMiddlewareInstance<unknown, ThunkDispatch<unknown, unknown, UnknownAction>, unknown>;

export const createAppInitListener: CreateAppInitListenerType = (apolloClient) => {
  const middleware = createListenerMiddleware();

  middleware.startListening({
    actionCreator: bootApp,
    effect: async (_action, { dispatch, getState, signal }) => {
      if (bootDispatched) return;
      bootDispatched = true;

      const authToken = (getState() as { application: ApplicationState }).application.authToken;

      notifyParentFrame(authToken);
      checkVpnBan(dispatch as AnyDispatch);

      if (authToken) {
        let initCompleted = false;

        const timeoutId = setTimeout(() => {
          if (!initCompleted && !signal.aborted) {
            logger.warn('⏰ App initialization timed out, falling back to idle');
            dispatch(setAppInitialized());
            dispatch(setAppStatus('idle'));
          }
        }, INIT_TIMEOUT_MS);

        await handleInit(dispatch as AnyDispatch, getState as GetState, signal);
        initCompleted = true;
        clearTimeout(timeoutId);
      } else {
        syncAnalytics(undefined);
        dispatch(setAppInitialized());
        dispatch(setAppStatus('idle'));
      }
    },
  });

  middleware.startListening({
    actionCreator: setAuthToken,
    effect: async (action, { dispatch, getState, signal, cancelActiveListeners }) => {
      cancelActiveListeners();
      notifyParentFrame(action.payload);

      if (action.payload) {
        await handleInit(dispatch as AnyDispatch, getState as GetState, signal);
      } else {
        await handleLogout(dispatch as AnyDispatch, signal, apolloClient);
      }
    },
  });

  middleware.startListening({
    matcher: fetchCurrentUserData.fulfilled.match,
    effect: async (action, { dispatch, getState, signal }) => {
      if (!hasInitialized) return;

      const newCompanyId = action.payload?.currentCompanyId;
      if (!newCompanyId || newCompanyId === lastInitializedCompanyId) return;

      const authToken = (getState() as { application: ApplicationState }).application.authToken;
      if (!authToken) return;

      logger.info('🔄 Company changed, re-initializing');
      await handleInit(dispatch as AnyDispatch, getState as GetState, signal);
    },
  });

  return middleware;
};
